/**
 * proofSlice — Redux Toolkit state for the proof review lifecycle
 * (PL-PROOFLOOP-001/004/006/007).
 *
 * Shared by both the storefront (customer proof view + decision + feedback
 * thread) and the console (admin proof view + release + revised-artwork
 * upload) — the two apps dispatch different subsets of the thunks below
 * against the same slice shape.
 *
 * Every rejected thunk preserves the full platform error envelope
 * (`message`, `severity`, `errors`, `errorCode`, `status`) via
 * {@link ProofError} rather than collapsing to a bare string, per
 * `.claude/rules/frontend-data-flow.md` §3.
 *
 * Side effects (toasts, navigation) belong in the consuming container —
 * never here.
 *
 * SH-PROOFLOOP-001.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  approveProof as apiApproveProof,
  fetchProof as apiFetchProof,
  fetchProofThread as apiFetchProofThread,
  requestProofChanges as apiRequestProofChanges,
} from '@dfq/shared/api/account/proof';
import {
  completeArtworkVersionUpload as apiCompleteArtworkVersionUpload,
  createArtworkFileVersion as apiCreateArtworkFileVersion,
  createDesignDraftArtwork as apiCreateDesignDraftArtwork,
  fetchAdminProof as apiFetchAdminProof,
  initiateArtworkVersionUpload as apiInitiateArtworkVersionUpload,
  releaseProof as apiReleaseProof,
} from '@dfq/shared/api/admin/proof';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  ArtworkFileVersionDTO,
  ArtworkVersionUploadCompleteDTO,
  ArtworkVersionUploadInitiateDTO,
  CreateArtworkFileVersionPayload,
  CreateDesignDraftArtworkPayload,
  OrderItemArtworkAttachmentDTO,
  ProofDecisionDTO,
  ProofDTO,
  ProofReleaseDTO,
  ProofThreadNoteDTO,
} from '@dfq/shared/types/proof';

// ---------------------------------------------------------------------------
// Error shape — preserves the full platform error envelope
// ---------------------------------------------------------------------------

export interface ProofError {
  message: string;
  severity?: string;
  errors?: Record<string, string[]>;
  errorCode?: string;
  status: number;
  /**
   * Structured detail from the platform. A 402 carries `decline_code` and
   * `outstanding_cents` here — without them the customer cannot be told
   * whether to confirm with the same card or find another.
   */
  context?: Record<string, unknown>;
}

function toProofError(err: unknown, fallbackMessage: string): ProofError {
  if (isApiError(err)) {
    return {
      message: err.message,
      severity: err.severity,
      errors: err.errors,
      errorCode: err.errorCode,
      status: err.status,
      context: err.context,
    };
  }
  const message = err instanceof Error ? err.message : fallbackMessage;
  return { message, status: 0 };
}

export type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface ProofState {
  proof: ProofDTO | null;
  proofStatus: AsyncStatus;
  proofError: ProofError | null;

  thread: ProofThreadNoteDTO[];
  threadStatus: AsyncStatus;
  threadError: ProofError | null;

  decisionStatus: AsyncStatus;
  decisionError: ProofError | null;
  lastDecision: ProofDecisionDTO | null;

  releaseStatus: AsyncStatus;
  releaseError: ProofError | null;
  lastRelease: ProofReleaseDTO | null;

  uploadInitiate: ArtworkVersionUploadInitiateDTO | null;
  uploadInitiateStatus: AsyncStatus;
  uploadInitiateError: ProofError | null;

  uploadComplete: ArtworkVersionUploadCompleteDTO | null;
  uploadCompleteStatus: AsyncStatus;
  uploadCompleteError: ProofError | null;

  versions: ArtworkFileVersionDTO[];
  versionCreateStatus: AsyncStatus;
  versionCreateError: ProofError | null;

  designDraftAttachment: OrderItemArtworkAttachmentDTO | null;
  designDraftCreateStatus: AsyncStatus;
  designDraftCreateError: ProofError | null;
}

export const initialState: ProofState = {
  proof: null,
  proofStatus: 'idle',
  proofError: null,

  thread: [],
  threadStatus: 'idle',
  threadError: null,

  decisionStatus: 'idle',
  decisionError: null,
  lastDecision: null,

  releaseStatus: 'idle',
  releaseError: null,
  lastRelease: null,

  uploadInitiate: null,
  uploadInitiateStatus: 'idle',
  uploadInitiateError: null,

  uploadComplete: null,
  uploadCompleteStatus: 'idle',
  uploadCompleteError: null,

  versions: [],
  versionCreateStatus: 'idle',
  versionCreateError: null,

  designDraftAttachment: null,
  designDraftCreateStatus: 'idle',
  designDraftCreateError: null,
};

// ---------------------------------------------------------------------------
// Thunks — customer
// ---------------------------------------------------------------------------

/** GET /api/v1/account/orders/{orderId}/proof */
export const fetchProofThunk = createAsyncThunk(
  'proof/fetchProof',
  async (orderId: number, { rejectWithValue }) => {
    try {
      return await apiFetchProof(orderId);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to fetch proof'));
    }
  },
);

/** POST /api/v1/account/orders/{orderId}/proof/approve */
export const approveProofThunk = createAsyncThunk(
  'proof/approveProof',
  async (
    args: number | { orderId: number; paymentMethodId?: string },
    { rejectWithValue },
  ) => {
    // Bare id keeps existing call sites working; the object form carries a
    // card the customer supplied after a decline.
    const orderId = typeof args === 'number' ? args : args.orderId;
    const paymentMethodId = typeof args === 'number' ? undefined : args.paymentMethodId;

    try {
      return await apiApproveProof(orderId, paymentMethodId);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to approve proof'));
    }
  },
);

/** POST /api/v1/account/orders/{orderId}/proof/request-changes */
export const requestProofChangesThunk = createAsyncThunk(
  'proof/requestProofChanges',
  async (args: { orderId: number; reason: string }, { rejectWithValue }) => {
    try {
      return await apiRequestProofChanges(args.orderId, args.reason);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to request proof changes'));
    }
  },
);

/** GET /api/v1/account/orders/{orderId}/proof/thread */
export const fetchProofThreadThunk = createAsyncThunk(
  'proof/fetchProofThread',
  async (orderId: number, { rejectWithValue }) => {
    try {
      return await apiFetchProofThread(orderId);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to fetch proof feedback thread'));
    }
  },
);

// ---------------------------------------------------------------------------
// Thunks — admin
// ---------------------------------------------------------------------------

/** GET /api/v1/admin/orders/{orderId}/proof */
export const fetchAdminProofThunk = createAsyncThunk(
  'proof/fetchAdminProof',
  async (orderId: number, { rejectWithValue }) => {
    try {
      return await apiFetchAdminProof(orderId);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to fetch proof'));
    }
  },
);

/** POST /api/v1/admin/orders/{orderId}/prepress/proof/release */
export const releaseProofThunk = createAsyncThunk(
  'proof/releaseProof',
  async (orderId: number, { rejectWithValue }) => {
    try {
      return await apiReleaseProof(orderId);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to release proof'));
    }
  },
);

/** POST /api/v1/admin/artwork/uploads/initiate-multipart */
export const initiateArtworkVersionUploadThunk = createAsyncThunk(
  'proof/initiateArtworkVersionUpload',
  async (
    params: { filename: string; fileSizeBytes: number; mimeType: string },
    { rejectWithValue },
  ) => {
    try {
      return await apiInitiateArtworkVersionUpload(params);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to initiate artwork upload'));
    }
  },
);

/** POST /api/v1/admin/artwork/uploads/complete-multipart */
export const completeArtworkVersionUploadThunk = createAsyncThunk(
  'proof/completeArtworkVersionUpload',
  async (params: { uploadId: string; s3Key: string }, { rejectWithValue }) => {
    try {
      return await apiCompleteArtworkVersionUpload(params);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to complete artwork upload'));
    }
  },
);

/** POST /api/v1/admin/artwork/{artworkFileId}/versions */
export const createArtworkFileVersionThunk = createAsyncThunk(
  'proof/createArtworkFileVersion',
  async (
    args: { artworkFileId: number; payload: CreateArtworkFileVersionPayload },
    { rejectWithValue },
  ) => {
    try {
      return await apiCreateArtworkFileVersion(args.artworkFileId, args.payload);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to record artwork version'));
    }
  },
);

/** POST /api/v1/admin/orders/{orderId}/prepress/design-draft-artwork */
export const createDesignDraftArtworkThunk = createAsyncThunk(
  'proof/createDesignDraftArtwork',
  async (
    args: { orderId: number; payload: CreateDesignDraftArtworkPayload },
    { rejectWithValue },
  ) => {
    try {
      return await apiCreateDesignDraftArtwork(args.orderId, args.payload);
    } catch (err) {
      return rejectWithValue(toProofError(err, 'Failed to upload design draft'));
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const proofSlice = createSlice({
  name: 'proof',
  initialState,
  reducers: {
    clearProofError(state) {
      state.proofError = null;
    },
    clearThreadError(state) {
      state.threadError = null;
    },
    clearDecisionError(state) {
      state.decisionError = null;
    },
    clearReleaseError(state) {
      state.releaseError = null;
    },
    clearUploadErrors(state) {
      state.uploadInitiateError = null;
      state.uploadCompleteError = null;
      state.versionCreateError = null;
    },
    resetUploadFlow(state) {
      state.uploadInitiate = null;
      state.uploadInitiateStatus = 'idle';
      state.uploadInitiateError = null;
      state.uploadComplete = null;
      state.uploadCompleteStatus = 'idle';
      state.uploadCompleteError = null;
      state.versionCreateStatus = 'idle';
      state.versionCreateError = null;
    },
    resetProof() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // ---- fetchProofThunk (customer) ----
    builder
      .addCase(fetchProofThunk.pending, (state) => {
        state.proofStatus = 'loading';
        state.proofError = null;
      })
      .addCase(fetchProofThunk.fulfilled, (state, action) => {
        state.proofStatus = 'succeeded';
        state.proof = action.payload;
      })
      .addCase(fetchProofThunk.rejected, (state, action) => {
        state.proofStatus = 'failed';
        state.proofError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch proof', status: 0 };
      });

    // ---- fetchAdminProofThunk (admin) ----
    builder
      .addCase(fetchAdminProofThunk.pending, (state) => {
        state.proofStatus = 'loading';
        state.proofError = null;
      })
      .addCase(fetchAdminProofThunk.fulfilled, (state, action) => {
        state.proofStatus = 'succeeded';
        state.proof = action.payload;
      })
      .addCase(fetchAdminProofThunk.rejected, (state, action) => {
        state.proofStatus = 'failed';
        state.proofError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch proof', status: 0 };
      });

    // ---- approveProofThunk ----
    builder
      .addCase(approveProofThunk.pending, (state) => {
        state.decisionStatus = 'loading';
        state.decisionError = null;
      })
      .addCase(approveProofThunk.fulfilled, (state, action) => {
        state.decisionStatus = 'succeeded';
        state.lastDecision = action.payload;
      })
      .addCase(approveProofThunk.rejected, (state, action) => {
        state.decisionStatus = 'failed';
        state.decisionError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to approve proof', status: 0 };
      });

    // ---- requestProofChangesThunk ----
    builder
      .addCase(requestProofChangesThunk.pending, (state) => {
        state.decisionStatus = 'loading';
        state.decisionError = null;
      })
      .addCase(requestProofChangesThunk.fulfilled, (state, action) => {
        state.decisionStatus = 'succeeded';
        state.lastDecision = action.payload;
      })
      .addCase(requestProofChangesThunk.rejected, (state, action) => {
        state.decisionStatus = 'failed';
        state.decisionError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to request proof changes', status: 0 };
      });

    // ---- fetchProofThreadThunk ----
    builder
      .addCase(fetchProofThreadThunk.pending, (state) => {
        state.threadStatus = 'loading';
        state.threadError = null;
      })
      .addCase(fetchProofThreadThunk.fulfilled, (state, action) => {
        state.threadStatus = 'succeeded';
        state.thread = action.payload;
      })
      .addCase(fetchProofThreadThunk.rejected, (state, action) => {
        state.threadStatus = 'failed';
        state.threadError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch proof feedback thread', status: 0 };
      });

    // ---- releaseProofThunk ----
    builder
      .addCase(releaseProofThunk.pending, (state) => {
        state.releaseStatus = 'loading';
        state.releaseError = null;
      })
      .addCase(releaseProofThunk.fulfilled, (state, action) => {
        state.releaseStatus = 'succeeded';
        state.lastRelease = action.payload;
      })
      .addCase(releaseProofThunk.rejected, (state, action) => {
        state.releaseStatus = 'failed';
        state.releaseError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to release proof', status: 0 };
      });

    // ---- initiateArtworkVersionUploadThunk ----
    builder
      .addCase(initiateArtworkVersionUploadThunk.pending, (state) => {
        state.uploadInitiateStatus = 'loading';
        state.uploadInitiateError = null;
      })
      .addCase(initiateArtworkVersionUploadThunk.fulfilled, (state, action) => {
        state.uploadInitiateStatus = 'succeeded';
        state.uploadInitiate = action.payload;
      })
      .addCase(initiateArtworkVersionUploadThunk.rejected, (state, action) => {
        state.uploadInitiateStatus = 'failed';
        state.uploadInitiateError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to initiate artwork upload', status: 0 };
      });

    // ---- completeArtworkVersionUploadThunk ----
    builder
      .addCase(completeArtworkVersionUploadThunk.pending, (state) => {
        state.uploadCompleteStatus = 'loading';
        state.uploadCompleteError = null;
      })
      .addCase(completeArtworkVersionUploadThunk.fulfilled, (state, action) => {
        state.uploadCompleteStatus = 'succeeded';
        state.uploadComplete = action.payload;
      })
      .addCase(completeArtworkVersionUploadThunk.rejected, (state, action) => {
        state.uploadCompleteStatus = 'failed';
        state.uploadCompleteError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to complete artwork upload', status: 0 };
      });

    // ---- createArtworkFileVersionThunk ----
    builder
      .addCase(createArtworkFileVersionThunk.pending, (state) => {
        state.versionCreateStatus = 'loading';
        state.versionCreateError = null;
      })
      .addCase(createArtworkFileVersionThunk.fulfilled, (state, action) => {
        state.versionCreateStatus = 'succeeded';
        state.versions.push(action.payload);
      })
      .addCase(createArtworkFileVersionThunk.rejected, (state, action) => {
        state.versionCreateStatus = 'failed';
        state.versionCreateError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to record artwork version', status: 0 };
      });

    // ---- createDesignDraftArtworkThunk ----
    builder
      .addCase(createDesignDraftArtworkThunk.pending, (state) => {
        state.designDraftCreateStatus = 'loading';
        state.designDraftCreateError = null;
      })
      .addCase(createDesignDraftArtworkThunk.fulfilled, (state, action) => {
        state.designDraftCreateStatus = 'succeeded';
        state.designDraftAttachment = action.payload;
      })
      .addCase(createDesignDraftArtworkThunk.rejected, (state, action) => {
        state.designDraftCreateStatus = 'failed';
        state.designDraftCreateError =
          (action.payload as ProofError | undefined) ??
          { message: action.error.message ?? 'Failed to upload design draft', status: 0 };
      });
  },
});

export const {
  clearProofError,
  clearThreadError,
  clearDecisionError,
  clearReleaseError,
  clearUploadErrors,
  resetUploadFlow,
  resetProof,
} = proofSlice.actions;

export { proofSlice };
export const proofReducer = proofSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface ProofRootState {
  proof: ProofState;
}

export const selectProof = (state: ProofRootState): ProofDTO | null => state.proof.proof;
export const selectProofStatus = (state: ProofRootState): AsyncStatus => state.proof.proofStatus;
export const selectProofError = (state: ProofRootState): ProofError | null =>
  state.proof.proofError;

export const selectProofThread = (state: ProofRootState): ProofThreadNoteDTO[] =>
  state.proof.thread;
export const selectProofThreadStatus = (state: ProofRootState): AsyncStatus =>
  state.proof.threadStatus;
export const selectProofThreadError = (state: ProofRootState): ProofError | null =>
  state.proof.threadError;

export const selectProofDecisionStatus = (state: ProofRootState): AsyncStatus =>
  state.proof.decisionStatus;
export const selectProofDecisionError = (state: ProofRootState): ProofError | null =>
  state.proof.decisionError;
export const selectLastProofDecision = (state: ProofRootState): ProofDecisionDTO | null =>
  state.proof.lastDecision;

export const selectProofReleaseStatus = (state: ProofRootState): AsyncStatus =>
  state.proof.releaseStatus;
export const selectProofReleaseError = (state: ProofRootState): ProofError | null =>
  state.proof.releaseError;
export const selectLastProofRelease = (state: ProofRootState): ProofReleaseDTO | null =>
  state.proof.lastRelease;

export const selectUploadInitiate = (
  state: ProofRootState,
): ArtworkVersionUploadInitiateDTO | null => state.proof.uploadInitiate;
export const selectUploadInitiateStatus = (state: ProofRootState): AsyncStatus =>
  state.proof.uploadInitiateStatus;
export const selectUploadInitiateError = (state: ProofRootState): ProofError | null =>
  state.proof.uploadInitiateError;

export const selectUploadComplete = (
  state: ProofRootState,
): ArtworkVersionUploadCompleteDTO | null => state.proof.uploadComplete;
export const selectUploadCompleteStatus = (state: ProofRootState): AsyncStatus =>
  state.proof.uploadCompleteStatus;
export const selectUploadCompleteError = (state: ProofRootState): ProofError | null =>
  state.proof.uploadCompleteError;

export const selectArtworkVersions = (state: ProofRootState): ArtworkFileVersionDTO[] =>
  state.proof.versions;
export const selectVersionCreateStatus = (state: ProofRootState): AsyncStatus =>
  state.proof.versionCreateStatus;
export const selectVersionCreateError = (state: ProofRootState): ProofError | null =>
  state.proof.versionCreateError;

export const selectDesignDraftAttachment = (
  state: ProofRootState,
): OrderItemArtworkAttachmentDTO | null => state.proof.designDraftAttachment;
export const selectDesignDraftCreateStatus = (state: ProofRootState): AsyncStatus =>
  state.proof.designDraftCreateStatus;
export const selectDesignDraftCreateError = (state: ProofRootState): ProofError | null =>
  state.proof.designDraftCreateError;
